import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Icon, Field, PrimaryButton } from "@/components/kit";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { api } from "@/api";
import { useI18n } from "@/i18n";

export default function AdminLogin() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPass, setNewPass] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const login = async () => {
    setLoading(true); setError("");
    try {
      await api.adminLogin(email.trim(), password);
      navigate("/admin/dashboard", { replace: true });
    } catch (e) {
      setError(e.message || t("wrong_creds"));
    } finally {
      setLoading(false);
    }
  };
  const doForgot = async () => {
    setLoading(true); setError(""); setInfo("");
    try { await api.forgotPassword(email); setMode("reset"); setInfo(t("forgot_sent")); }
    catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  const doReset = async () => {
    setLoading(true); setError(""); setInfo("");
    try {
      await api.resetPassword(email, code, newPass);
      setInfo(t("reset_done")); setMode("login"); setPassword(""); setCode(""); setNewPass("");
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen flex-col bg-paper">
      <div className="mx-auto flex w-full max-w-md items-center justify-between px-5 pt-5">
        <Link to="/" data-testid="login-back-home" className="flex items-center gap-1.5 text-[14px] text-ink-3 hover:text-brand">
          <Icon name="arrow-left" size={16} /> {t("nav_back_home")}
        </Link>
        <LanguageSwitcher />
      </div>

      <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-5 pb-16">
        <div className="mb-6 text-center">
          <img src="/logo.png" alt="Déco Ligot" className="mx-auto mb-5 h-11 object-contain" />
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-brand-tertiary">
            <Icon name="lock" size={28} className="text-brand" />
          </div>
          <h1 className="font-display text-3xl font-semibold text-ink">{t("pro_title")}</h1>
          <p className="mt-1 text-[14px] text-mute">{t("pro_sub")}</p>
        </div>

        <div className="rounded-2xl border border-line bg-white p-6">
          <Field label={t("admin_email")} testId="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@decoligot.be" />
          {mode === "login" && (
            <Field label={t("admin_pw")} testId="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
              onKeyDown={(e) => { if (e.key === "Enter" && email && password) login(); }} />
          )}
          {mode === "reset" && (
            <>
              <Field label={t("reset_code")} testId="admin-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" />
              <Field label={t("reset_newpw")} testId="admin-newpw" type="password" value={newPass} onChange={(e) => setNewPass(e.target.value)} placeholder="••••••••" />
            </>
          )}
          {mode === "forgot" && <p className="mb-3 text-[13px] text-mute">{t("forgot_help")}</p>}
          {error ? <p data-testid="admin-error" className="mb-3 text-[14px] font-medium text-err">{error}</p> : null}
          {info ? <p data-testid="admin-info" className="mb-3 text-[14px] font-medium text-ok">{info}</p> : null}

          {mode === "login" && (
            <>
              <PrimaryButton testId="admin-login-btn" title={t("admin_login")} icon="log-in" onClick={login} loading={loading} disabled={!email || !password} />
              <button data-testid="forgot-link" onClick={() => { setMode("forgot"); setError(""); setInfo(""); }} className="mt-4 w-full text-center text-[14px] font-semibold text-brand">
                {t("forgot_link")}
              </button>
            </>
          )}
          {mode === "forgot" && (
            <>
              <PrimaryButton testId="forgot-btn" title={t("forgot_send")} icon="mail" onClick={doForgot} loading={loading} disabled={!email} />
              <button data-testid="forgot-back" onClick={() => { setMode("login"); setError(""); }} className="mt-4 w-full text-center text-[14px] font-semibold text-brand">
                {t("forgot_back")}
              </button>
            </>
          )}
          {mode === "reset" && (
            <>
              <PrimaryButton testId="reset-btn" title={t("reset_submit")} icon="check" onClick={doReset} loading={loading} disabled={!email || !code || !newPass} />
              <button data-testid="reset-back" onClick={() => { setMode("login"); setError(""); }} className="mt-4 w-full text-center text-[14px] font-semibold text-brand">
                {t("forgot_back")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
